/**
 * Get Your Answer — "Ask your own question" page.
 *
 * Renders when a user submits their OWN typed question (vs. picking a
 * canonical from the library). Matches the design from the source's
 * question.html: two-column article + sticky CTA card, ivory background,
 * 3-bullet "what the CPA will do" prompt, trust strip in sidebar,
 * disclaimer at the bottom, and a "similar canonical questions" section
 * powered by the live MongoDB search index.
 *
 * Route: /get-your-answer/ask?q=<the-user's-question>
 *
 * Entry points:
 *   - Library "Describe your question" form (when search returns 0)
 *   - Anywhere the user types their own custom inquiry
 *
 * Spec: see /tmp/schedule_a_meet/KGOB-Ask-a-CPA-offline/question_structure.html
 *       and SEO-013 in /app/docs/SEO_UI_CONSULTATION_LOG.md
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ArrowRight, ShieldCheck, FileCheck2, BadgeCheck,
  CheckCircle2, ListChecks, Lightbulb, Compass,
} from 'lucide-react';
import { buildOutboundUrl, trackCTA } from '@/lib/getYourAnswerApi';
import { searchLibrary, describeQuestion } from '@/lib/getYourAnswerLibraryApi';

export default function GetYourAnswerAsk() {
  const [searchParams] = useSearchParams();
  const question = (searchParams.get('q') || '').trim();

  const [similar, setSimilar] = useState([]);
  const [emailValue, setEmailValue] = useState('');
  const [logSubmitted, setLogSubmitted] = useState(false);
  // Custom-question pages default to the entry-level $95 tier but let users
  // pick Deep Dive or Strategy if their situation is bigger.
  const [selectedTier, setSelectedTier] = useState('quick');

  const SESSION_LABEL = {
    quick:    { name: 'Quick Question',  min: 30, price: 95 },
    deep_dive:{ name: 'Deep Dive',        min: 60, price: 170 },
    strategy: { name: 'Strategy Session', min: 90, price: 240 },
  };
  const effectiveTier = SESSION_LABEL[selectedTier] || SESSION_LABEL.quick;

  // On mount: log the custom question to gya_described_queries (feeds nightly AI expander)
  // and fetch up to 5 similar canonical questions from the live library.
  useEffect(() => {
    if (!question) return;
    // Fire-and-forget log so the AI nightly job learns from this question
    describeQuestion(question, null).catch(() => { /* ignore */ });
    // Pull canonical neighbors via MongoDB text search
    searchLibrary(question, { limit: 5 }).then((r) => {
      setSimilar(r.results || []);
    });
    // Track the page view as a CTA so app.kgob.com sees the funnel signal
    trackCTA('ask-page-view', { question: question.slice(0, 120) });
    // Explicitly set document.title + fire a corrected GA page_view. The global
    // RouteChangeTracker fires too early on dynamic pages (before query-param
    // state is resolved), so GA would otherwise record the placeholder title.
    const titleSnippet = question.length > 50 ? `${question.slice(0, 50)}...` : question;
    const fullTitle = `${titleSnippet} — Ask a CPA · KGOB`;
    document.title = fullTitle;
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'page_view', {
          page_path: window.location.pathname + window.location.search,
          page_location: window.location.href,
          page_title: fullTitle,
          send_to: 'G-KYMCLPZQKT',
        });
      } catch (e) { /* analytics never throws */ }
    }
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      try {
        window.dataLayer.push({
          event: 'spa_pageview',
          page_path: window.location.pathname + window.location.search,
          page_location: window.location.href,
          page_title: fullTitle,
        });
      } catch (e) { /* no-op */ }
    }
  }, [question]);

  if (!question) {
    // No question = redirect to the library
    return <Navigate to="/get-your-answer/library" replace />;
  }

  const bookThis = (source) => {
    trackCTA(source, { question: question.slice(0, 120), session_type: selectedTier });
    // Custom question → no slug + no topic; pass question text as utm_term
    // for attribution. The user types their question again on Step 1 of the
    // app.kgob.com funnel (their spec doesn't accept a question pre-fill).
    window.location.href = buildOutboundUrl(source, {
      questionText: question,
      session_type: selectedTier,
      campaign: 'custom_question',
    });
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!emailValue.trim()) return;
    setLogSubmitted(true);
    await describeQuestion(question, emailValue.trim());
    trackCTA('ask-email-followup', { question: question.slice(0, 120) });
  };

  return (
    <div className="min-h-screen bg-[#faf9f5]" data-testid="get-your-answer-ask-page">
      <Helmet>
        <title>{`${question.slice(0, 50)}${question.length > 50 ? '…' : ''} — Ask a CPA · KGOB`}</title>
        <meta
          name="description"
          content="Bring your exact tax question to a licensed Charlotte CPA firm. One flat fee, 30-minute focused session — no commitment."
        />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <Header />

      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10 sm:py-14">
        {/* ── Breadcrumb ── */}
        <nav className="flex items-center gap-2 text-sm text-[#4C5A70]/70 mb-6" aria-label="Breadcrumb" data-testid="ask-breadcrumb">
          <Link data-testid="get-your-answer-ask-link-3" to="/get-your-answer" className="hover:text-[#0B2240] transition-colors">Get Your Answer</Link>
          <span>/</span>
          <Link data-testid="get-your-answer-ask-link-2" to="/get-your-answer/library" className="hover:text-[#0B2240] transition-colors">Library</Link>
          <span>/</span>
          <span className="text-[#0B2240] font-semibold">Your question</span>
        </nav>

        {/* ── Two-column layout: article + sticky CTA ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          {/* ── Article ── */}
          <article>
            <p className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold mb-3" data-testid="ask-eyebrow">
              Your question
            </p>

            <h1
              className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-[-0.01em] font-semibold text-[#0B2240] mb-5"
              data-testid="ask-title"
            >
              &ldquo;{question}&rdquo;
            </h1>

            {/* Chips */}
            <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="ask-chips">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold border border-[#2FA3B5]/40 bg-[#2FA3B5]/5 text-[#2FA3B5]">
                Common
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold border border-[#0B2240]/15 text-[#0B2240]/70">
                Quick Question · 30 min · $95
              </span>
            </div>

            {/* Framing */}
            <p className="text-[#4C5A70] text-base sm:text-lg leading-relaxed mb-8" data-testid="ask-framing">
              You don&rsquo;t have to fit your situation into a pre-written question. Bring it
              in your own words and a CPA at our firm will handle it — no jargon, no judgment.
            </p>

            {/* 3-bullet prompt — straight from question.html */}
            <div className="mt-2">
              <p className="text-[#0B2240] font-semibold mb-4">
                In your <span className="text-[#D4AF37]">30-minute session</span>, the KGOB advisor handling it will:
              </p>
              <ul className="space-y-4" data-testid="ask-points">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                    <Compass className="w-5 h-5" />
                  </span>
                  <div className="pt-1">
                    <b className="text-[#0B2240]">Read your exact situation</b>
                    <span className="text-[#4C5A70]"> and tell you, in plain English, what&rsquo;s actually going on.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                    <ListChecks className="w-5 h-5" />
                  </span>
                  <div className="pt-1">
                    <b className="text-[#0B2240]">Lay out your options</b>
                    <span className="text-[#4C5A70]"> and the trade-offs — no jargon, no judgment.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                    <Lightbulb className="w-5 h-5" />
                  </span>
                  <div className="pt-1">
                    <b className="text-[#0B2240]">Give you a clear next step</b>
                    <span className="text-[#4C5A70]"> you can act on, whether that&rsquo;s with us or on your own.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* ── Similar canonical questions ── */}
            {similar.length > 0 && (
              <section className="mt-12 pt-10 border-t border-[#E1DFD7]" data-testid="ask-similar-section">
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-8 bg-[#D4AF37]" />
                  <span className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">
                    Closely related from our library
                  </span>
                </div>
                <p className="text-[#4C5A70] text-sm mb-5">
                  These canonical questions match what you typed — handy if you want to read more context before booking.
                </p>
                <ul className="space-y-2" data-testid="ask-similar-list">
                  {similar.map((q) => {
                    const topicSlug = (q.topic_id || '').replace(/_/g, '-');
                    return (
                      <li key={q.id}>
                        <Link
                          to={`/get-your-answer/${topicSlug}/${q.slug}`}
                          onClick={() => trackCTA('ask-similar-card', { question: q.slug, topic: q.topic_id })}
                          className="group block p-4 rounded-xl bg-white border border-[#E1DFD7] hover:border-[#D4AF37] hover:shadow-sm transition-all"
                          data-testid={`ask-similar-${q.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-[#0B2240] text-sm leading-snug">&ldquo;{q.text}&rdquo;</p>
                              <p className="text-[10px] tracking-[0.16em] uppercase font-semibold text-[#4C5A70]/65 mt-2">
                                {q.topic_label}
                              </p>
                            </div>
                            <ArrowRight className="w-3.5 h-3.5 text-[#4C5A70]/40 group-hover:text-[#0B2240] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-5">
                  <Link data-testid="get-your-answer-ask-link-1"
                    to="/get-your-answer/library"
                    onClick={() => trackCTA('ask-browse-library', {})}
                    className="inline-flex items-center gap-1.5 text-sm text-[#0B2240] hover:text-[#D4AF37] font-semibold underline-offset-4 hover:underline"
                  >
                    Browse the full question library
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </p>
              </section>
            )}

            {/* ── Disclaimer ── */}
            <p className="mt-12 text-[12px] leading-relaxed text-[#4C5A70]/70" data-testid="ask-disclaimer">
              This page is a prompt to start a conversation, not tax or legal advice, and states
              no tax-law specifics as fact. A consult session does not by itself create an
              ongoing engagement. We do not promise specific outcomes or savings. Kohari
              Gonzalez Oneyear &amp; Brown PLLC — Charlotte, NC.
            </p>

            <Link
              to="/get-your-answer/library"
              className="inline-flex items-center gap-2 mt-6 text-sm text-[#4C5A70] hover:text-[#0B2240] transition-colors"
              data-testid="ask-back-link"
            >
              <ChevronLeft className="w-4 h-4" /> Back to the full library
            </Link>
          </article>

          {/* ── Sidebar CTA card (sticky on lg+) ── */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-2xl border border-[#E1DFD7] p-6 shadow-sm" data-testid="ask-cta-card">
              <p className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold mb-3">
                Bring this to KGOB
              </p>

              <div className="flex items-baseline gap-2 mb-3" data-testid="ask-cta-price">
                <span className="font-['Playfair_Display'] text-4xl font-bold text-[#0B2240]">${effectiveTier.price}</span>
                <span className="text-sm text-[#4C5A70]">/ {effectiveTier.min}-minute {effectiveTier.name}</span>
              </div>

              <p className="text-[11px] text-[#4C5A70]/80 leading-relaxed mb-4">
                Answered by Kohari Gonzalez Oneyear &amp; Brown — a licensed NC CPA firm.
              </p>

              {/* 3-tier selector — start with $95 default, let user upgrade if needed */}
              <div className="space-y-2 mb-5" role="radiogroup" aria-label="Session length" data-testid="ask-tier-picker">
                {(['quick', 'deep_dive', 'strategy']).map((key) => {
                  const tier = SESSION_LABEL[key];
                  const isSelected = selectedTier === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelectedTier(key)}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-all ${
                        isSelected
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-[#E1DFD7] bg-white hover:border-[#D4AF37]/60'
                      }`}
                      data-testid={`ask-tier-${key}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                            isSelected ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-[#4C5A70]/30'
                          }`} />
                          <span className="text-[13px] font-semibold text-[#0B2240]">{tier.name}</span>
                        </div>
                        <span className="text-[13px] text-[#0B2240] font-semibold whitespace-nowrap">
                          ${tier.price}
                        </span>
                      </div>
                      <p className="text-[11px] text-[#4C5A70]/80 mt-0.5 pl-[22px]">
                        {tier.min} minutes
                      </p>
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={() => bookThis('ask-primary-cta')}
                className="w-full h-12 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110"
                data-testid="ask-book-cta"
              >
                Book the {effectiveTier.name} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              {/* Trust strip */}
              <ul className="mt-5 space-y-2 text-[11px] text-[#4C5A70]" data-testid="ask-trust-strip">
                <li className="flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                  <span>KGOB — a licensed Charlotte CPA firm</span>
                </li>
                <li className="flex items-center gap-2">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                  <span>One flat fee — no commitment</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileCheck2 className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                  <span>Documents handled securely</span>
                </li>
              </ul>
            </div>

            {/* "Not ready yet" — capture email for follow-up */}
            <div className="mt-5 bg-[#F4EFE3] rounded-xl border border-[#E1DFD7] p-5" data-testid="ask-followup-card">
              {!logSubmitted ? (
                <form data-testid="get-your-answer-ask-form-1" onSubmit={submitEmail}>
                  <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#0B2240] mb-2">
                    Not ready to book?
                  </p>
                  <p className="text-[12px] text-[#4C5A70] leading-relaxed mb-3">
                    Leave your email and we&rsquo;ll add an answer to the library and let you know
                    when it&rsquo;s live.
                  </p>
                  <input
                    type="email"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2 rounded-lg border border-[#E1DFD7] bg-white text-sm text-[#0B2240] focus:outline-none focus:border-[#D4AF37] mb-3"
                    data-testid="ask-email-input"
                    required
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    className="w-full border-[#D4AF37] text-[#0B2240] hover:bg-[#D4AF37]/10"
                    data-testid="ask-email-submit"
                  >
                    Notify me
                  </Button>
                </form>
              ) : (
                <div className="flex items-start gap-2" data-testid="ask-followup-done">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[12px] font-semibold text-[#0B2240] mb-1">Thanks — we got it.</p>
                    <p className="text-[11px] text-[#4C5A70] leading-relaxed">
                      We&rsquo;ll add the answer to the library and email you when it&rsquo;s live.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
